import { useState, useEffect, useMemo } from 'react'
import { useAuthStore, usePlayerStore } from '../stores'
import { apiClient } from '../utils/api'
import { Bell, Eye, Bookmark, MessageSquare, Mail, CheckCircle2, Clock, Trash2, CheckCheck, AlertCircle } from 'lucide-react'

interface PlayerNotification {
  id: string
  user_id: string
  type: 'profile_view' | 'profile_save' | 'message' | 'trial_invitation' | 'verification_change' | 'announcement' | 'general'
  title: string
  message: string
  actor_name?: string
  actor_role?: string
  is_read: boolean
  link?: string
  created_at?: string
}

const NOTIFICATION_ICONS: Record<string, { icon: React.ElementType; color: string; bg: string }> = {
  profile_view: { icon: Eye, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  profile_save: { icon: Bookmark, color: 'text-orange-400', bg: 'bg-orange-500/10' },
  message: { icon: MessageSquare, color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
  trial_invitation: { icon: Mail, color: 'text-purple-400', bg: 'bg-purple-500/10' },
  verification_change: { icon: CheckCircle2, color: 'text-green-400', bg: 'bg-green-500/10' },
  announcement: { icon: Bell, color: 'text-red-400', bg: 'bg-red-500/10' },
  general: { icon: Bell, color: 'text-gray-400', bg: 'bg-gray-500/10' },
}

function PlayerNotifications() {
  const { user } = useAuthStore()
  const { players } = usePlayerStore()
  const [notifications, setNotifications] = useState<PlayerNotification[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<'all' | 'unread'>('all')

  const currentPlayer = useMemo(() => {
    if (!user) return null
    return players.find(p =>
      p.scout?.toLowerCase() === user.email?.toLowerCase() ||
      p.email?.toLowerCase() === user.email?.toLowerCase() ||
      p.name?.toLowerCase() === user.name?.toLowerCase()
    ) || null
  }, [user, players])

  useEffect(() => {
    if (currentPlayer?.id) {
      fetchNotifications(currentPlayer.id)
    }
  }, [currentPlayer?.id])

  const fetchNotifications = async (playerId: string) => {
    setLoading(true)
    setError(null)
    try {
      const data = await apiClient.getPlayerNotifications(playerId)
      setNotifications(data || [])
    } catch (err) {
      setError('Failed to load notifications')
      console.error('Failed to fetch notifications:', err)
    } finally {
      setLoading(false)
    }
  }

  const markAsRead = async (id: string) => {
    try {
      await apiClient.markPlayerNotificationAsRead(id)
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, is_read: true } : n)
      )
    } catch (err) {
      console.error('Failed to mark notification as read:', err)
    }
  }

  const markAllAsRead = async () => {
    if (!currentPlayer?.id) return
    try {
      await apiClient.markAllPlayerNotificationsAsRead(currentPlayer.id)
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })))
    } catch (err) {
      console.error('Failed to mark all as read:', err)
    }
  }

  const deleteNotification = async (id: string) => {
    try {
      await apiClient.deletePlayerNotification(id)
      setNotifications(prev => prev.filter(n => n.id !== id))
    } catch (err) {
      console.error('Failed to delete notification:', err)
    }
  }

  const filtered = useMemo(() => {
    if (filter === 'unread') return notifications.filter(n => !n.is_read)
    return notifications
  }, [notifications, filter])

  const unreadCount = notifications.filter(n => !n.is_read).length

  const formatTime = (dateStr?: string) => {
    if (!dateStr) return ''
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString()
  }

  if (loading) {
    return (
      <div className="rounded-3xl border border-white/10 bg-white/5 p-8 text-center">
        <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-gray-400 text-sm">Loading notifications...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-3xl border border-red-500/20 bg-red-500/5 p-8 text-center">
        <AlertCircle className="mx-auto text-red-400 mb-4" size={48} />
        <h3 className="text-lg font-semibold text-white">Error</h3>
        <p className="text-red-300 mt-2">{error}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bell size={18} className="text-green-500" />
          <h3 className="text-xl font-bold text-white">Notifications</h3>
          {unreadCount > 0 && (
            <span className="px-2 py-0.5 text-[10px] font-black uppercase tracking-widest bg-green-500/20 text-green-400 rounded-full">
              {unreadCount}
            </span>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllAsRead}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-green-400 hover:text-green-300 transition"
          >
            <CheckCheck size={14} />
            Mark all read
          </button>
        )}
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2">
        {[
          { id: 'all' as const, label: 'All', count: notifications.length },
          { id: 'unread' as const, label: 'Unread', count: unreadCount },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setFilter(tab.id)}
            className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition border ${
              filter === tab.id
                ? 'bg-green-500/10 border-green-500/30 text-green-400'
                : 'bg-white/5 border-white/10 text-gray-400 hover:text-white'
            }`}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {/* Notifications List */}
      {filtered.length === 0 ? (
        <div className="rounded-3xl border border-white/10 bg-white/5 p-12 text-center">
          <Bell className="mx-auto text-gray-600 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-white">
            {filter === 'unread' ? 'All caught up!' : 'No notifications yet'}
          </h3>
          <p className="text-gray-400 mt-2">
            {filter === 'unread'
              ? 'You have no unread notifications.'
              : 'When someone interacts with your profile, you\'ll see it here.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((notif) => {
            const typeConfig = NOTIFICATION_ICONS[notif.type] || NOTIFICATION_ICONS.general
            const TypeIcon = typeConfig.icon

            return (
              <div
                key={notif.id}
                className={`bg-[#161b22] border rounded-2xl p-4 transition hover:border-green-500/20 ${
                  notif.is_read ? 'border-gray-800' : 'border-green-500/30 bg-green-500/5'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-xl ${typeConfig.bg} flex items-center justify-center flex-shrink-0`}>
                    <TypeIcon size={18} className={typeConfig.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-semibold text-white">{notif.title}</p>
                      {!notif.is_read && (
                        <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0 mt-1.5" />
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-1 line-clamp-2">{notif.message}</p>
                    <p className="text-[10px] text-gray-600 uppercase tracking-widest mt-2 flex items-center gap-1">
                      <Clock size={10} />
                      {formatTime(notif.created_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!notif.is_read && (
                      <button
                        onClick={() => markAsRead(notif.id)}
                        className="p-1.5 text-gray-500 hover:text-green-400 transition rounded-lg hover:bg-green-500/10"
                        title="Mark as read"
                      >
                        <CheckCircle2 size={14} />
                      </button>
                    )}
                    <button
                      onClick={() => deleteNotification(notif.id)}
                      className="p-1.5 text-gray-500 hover:text-red-400 transition rounded-lg hover:bg-red-500/10"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default PlayerNotifications
